/**
 * Outil « Table de Pythagore » (Boîte à outils, 6e → 3e).
 *
 * Glisser le doigt sur la grille choisit a × b : le rectangle a × b se colore
 * (a lignes de b cases), le produit et une astuce s'affichent. Les boutons
 * − / + font la même chose au clavier et pour les petits doigts (cases de la
 * grille < 44 px sur un téléphone). Défi : 20 produits en une minute, au
 * pavé numérique. Calculs purs dans `times-table.ts`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { Delete } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import { Stepper } from '../../ui/controls';
import type { ToolProps } from '../../tool-types';
import {
  CHALLENGE_LENGTH,
  CHALLENGE_SECONDS,
  TABLE_SIZE,
  challengeSummary,
  clampFactor,
  isRightAnswer,
  makeQuestions,
  tipFor,
  typeDigit,
  type TableQuestion,
} from './times-table';

const RANGE = Array.from({ length: TABLE_SIZE }, (_, i) => i + 1);

export function TimesTableTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [a, setA] = useState(7);
  const [b, setB] = useState(8);
  const [hidden, setHidden] = useState(false);
  const [challenge, setChallenge] = useState(false);
  const p = a * b;

  if (challenge) {
    return <TableChallenge rng={rng} onQuit={() => setChallenge(false)} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={hidden}
          onClick={() => setHidden((v) => !v)}
          className={cn(
            'min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
            hidden
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-600 hover:bg-ink-50',
          )}
        >
          Masquer les produits
        </button>
        <button
          type="button"
          onClick={() => setChallenge(true)}
          className="bg-primary-500 hover:bg-primary-600 ocean:hover:bg-primary-400 min-h-11 rounded-xl px-4 text-sm font-bold text-white active:scale-[0.98]"
        >
          Défi : 20 produits en 1 min
        </button>
      </div>

      {/* le produit choisi */}
      <section aria-live="polite" className="bg-ink-50 rounded-2xl p-3">
        <p className="font-display text-ink-900 text-2xl font-bold" data-testid="times-product">
          {a} × {b} = {p}
        </p>
        <p className="text-ink-500 text-sm">
          {a === b ? `Un carré : ${a}² = ${p}` : `et aussi ${b} × ${a} = ${p}`}
        </p>
        <p className="text-ink-700 mt-1 text-sm">{tipFor(a, b)}</p>
        {onSend && (
          <SendToSheetButton
            className="mt-2"
            label={String(p)}
            cost={sendCost}
            onClick={() => onSend({ atoms: [String(p)] })}
          />
        )}
      </section>

      <div className="grid grid-cols-2 gap-2">
        <Stepper
          label="Lignes"
          value={a}
          min={1}
          max={TABLE_SIZE}
          onChange={(v) => setA(clampFactor(v))}
        />
        <Stepper
          label="Colonnes"
          value={b}
          min={1}
          max={TABLE_SIZE}
          onChange={(v) => setB(clampFactor(v))}
        />
      </div>

      <TableGrid
        a={a}
        b={b}
        hidden={hidden}
        onPick={(r, c) => {
          setA(r);
          setB(c);
        }}
      />
    </div>
  );
}

/** 11 × 11 : en-têtes 1..10 sur la 1re ligne et la 1re colonne. */
function TableGrid({
  a,
  b,
  hidden,
  onPick,
}: {
  a: number;
  b: number;
  hidden: boolean;
  onPick: (row: number, col: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const pickAt = (e: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const cell = r.width / (TABLE_SIZE + 1);
    const col = Math.floor((e.clientX - r.left) / cell);
    const row = Math.floor((e.clientY - r.top) / cell);
    onPick(clampFactor(row), clampFactor(col));
  };

  return (
    <div
      ref={ref}
      role="img"
      aria-label={`Table de Pythagore : rectangle de ${a} lignes et ${b} colonnes, ${a * b} cases`}
      className="grid w-full max-w-[26rem] touch-none select-none gap-px"
      style={{ gridTemplateColumns: `repeat(${TABLE_SIZE + 1}, minmax(0, 1fr))` }}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        pickAt(e);
      }}
      onPointerMove={(e) => {
        if (dragging.current) pickAt(e);
      }}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <span className="text-ink-400 flex aspect-square items-center justify-center text-xs font-bold">
        ×
      </span>
      {RANGE.map((c) => (
        <span
          key={`h${c}`}
          className={cn(
            'flex aspect-square items-center justify-center text-xs font-bold',
            c === b ? 'text-primary-600' : 'text-ink-500',
          )}
        >
          {c}
        </span>
      ))}
      {RANGE.map((r) => (
        <Row key={r} r={r} a={a} b={b} hidden={hidden} />
      ))}
    </div>
  );
}

function Row({ r, a, b, hidden }: { r: number; a: number; b: number; hidden: boolean }) {
  return (
    <>
      <span
        className={cn(
          'flex aspect-square items-center justify-center text-xs font-bold',
          r === a ? 'text-primary-600' : 'text-ink-500',
        )}
      >
        {r}
      </span>
      {RANGE.map((c) => {
        const inside = r <= a && c <= b;
        const corner = r === a && c === b;
        return (
          <span
            key={c}
            className={cn(
              'flex aspect-square items-center justify-center rounded-[4px] text-xs tabular-nums',
              corner
                ? 'bg-primary-500 font-bold text-white'
                : inside
                  ? 'bg-primary-100 text-primary-700'
                  : r === c
                    ? 'bg-xp-50 text-ink-600'
                    : 'bg-ink-50 text-ink-500',
            )}
          >
            {hidden && !corner ? '' : r * c}
          </span>
        );
      })}
    </>
  );
}

// ── Défi ────────────────────────────────────────────────────────────────────

function TableChallenge({ rng, onQuit }: { rng: () => number; onQuit: () => void }) {
  const [questions, setQuestions] = useState<TableQuestion[]>(() => makeQuestions(rng));
  const [index, setIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [last, setLast] = useState<{ q: TableQuestion; given: string; ok: boolean } | null>(null);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [endedAt, setEndedAt] = useState<number | null>(null);

  const elapsed = ((endedAt ?? now) - startedAt) / 1000;
  const left = Math.max(0, CHALLENGE_SECONDS - elapsed);
  const over = endedAt !== null;
  const q = questions[index];

  useEffect(() => {
    if (over) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [over]);

  useEffect(() => {
    if (!over && left <= 0) setEndedAt(startedAt + CHALLENGE_SECONDS * 1000);
  }, [left, over, startedAt]);

  const submit = useCallback(() => {
    if (over || !q || input === '') return;
    const ok = isRightAnswer(q, input);
    setLast({ q, given: input, ok });
    if (ok) setScore((s) => s + 1);
    setInput('');
    if (index + 1 >= questions.length) setEndedAt(Date.now());
    else setIndex(index + 1);
  }, [over, q, input, index, questions.length]);

  const restart = () => {
    setQuestions(makeQuestions(rng));
    setIndex(0);
    setInput('');
    setScore(0);
    setLast(null);
    const t = Date.now();
    setStartedAt(t);
    setNow(t);
    setEndedAt(null);
  };

  // clavier physique (ordinateur)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (over) return;
      if (/^[0-9]$/.test(e.key)) setInput((v) => typeDigit(v, e.key));
      else if (e.key === 'Backspace') setInput((v) => v.slice(0, -1));
      else if (e.key === 'Enter') submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [over, submit]);

  const keys = useMemo(() => ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'], []);

  return (
    <div className="space-y-4" data-testid="times-challenge">
      <div className="flex items-center justify-between gap-3">
        <p className="text-ink-700 text-sm font-semibold">
          {over
            ? 'Terminé'
            : `Question ${Math.min(index + 1, questions.length)}/${questions.length}`}{' '}
          · {score} juste{score > 1 ? 's' : ''}
        </p>
        <button
          type="button"
          onClick={onQuit}
          className="border-ink-200 text-ink-700 min-h-11 rounded-xl border px-3 text-sm font-semibold active:scale-[0.98]"
        >
          Quitter le défi
        </button>
      </div>
      <div
        className="bg-ink-100 h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-label="Temps restant"
        aria-valuemin={0}
        aria-valuemax={CHALLENGE_SECONDS}
        aria-valuenow={Math.ceil(left)}
      >
        <div
          className="bg-primary-500 h-full transition-[width] duration-200"
          style={{ width: `${(left / CHALLENGE_SECONDS) * 100}%` }}
        />
      </div>

      {over ? (
        <section className="bg-ink-50 space-y-3 rounded-2xl p-4 text-center" aria-live="polite">
          <p className="font-display text-ink-900 text-2xl font-bold">
            {challengeSummary(score, CHALLENGE_LENGTH, Math.min(elapsed, CHALLENGE_SECONDS))}
          </p>
          <p className="text-ink-600 text-sm">
            {score === CHALLENGE_LENGTH
              ? 'Sans faute ! Essaie de battre ton temps.'
              : 'Rejoue pour faire mieux : les tables se retiennent à force de les croiser.'}
          </p>
          <button
            type="button"
            onClick={restart}
            className="bg-primary-500 hover:bg-primary-600 ocean:hover:bg-primary-400 min-h-11 w-full rounded-xl px-4 text-sm font-bold text-white active:scale-[0.98]"
          >
            Rejouer
          </button>
        </section>
      ) : (
        q && (
          <>
            <p
              className="font-display text-ink-900 text-center text-4xl font-bold tabular-nums"
              aria-live="polite"
            >
              {q.a} × {q.b} = <span className="text-primary-600">{input || '?'}</span>
            </p>
            {last && (
              <p
                className={cn(
                  'text-center text-sm font-semibold',
                  last.ok ? 'text-success-600' : 'text-error-600',
                )}
              >
                {last.ok
                  ? `Juste : ${last.q.a} × ${last.q.b} = ${last.q.a * last.q.b}`
                  : `${last.q.a} × ${last.q.b} = ${last.q.a * last.q.b} (pas ${last.given})`}
              </p>
            )}
            <div className="mx-auto grid max-w-[18rem] grid-cols-3 gap-2">
              {keys.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-label={k === 'del' ? 'Effacer' : k === 'ok' ? 'Valider' : k}
                  onClick={() => {
                    if (k === 'del') setInput((v) => v.slice(0, -1));
                    else if (k === 'ok') submit();
                    else setInput((v) => typeDigit(v, k));
                  }}
                  className={cn(
                    'font-display flex h-14 items-center justify-center rounded-2xl text-xl font-bold transition-colors active:scale-[0.95]',
                    k === 'ok'
                      ? 'bg-primary-500 hover:bg-primary-600 ocean:hover:bg-primary-400 text-white'
                      : k === 'del'
                        ? 'bg-ink-200/70 text-ink-700'
                        : 'bg-ink-100 text-ink-900 hover:bg-ink-200',
                  )}
                >
                  {k === 'del' ? <Delete className="h-5 w-5" aria-hidden /> : k === 'ok' ? 'OK' : k}
                </button>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}
